import type { PatternFormFieldItemTag } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';
import type { Except } from 'type-fest';

import { ClientError } from '@ricoh/ria.client.core/errors';
import { newKey } from '@ricoh/ria.client.core/utils/data/key';
import { omit } from '@ricoh/ria.client.core/utils/data/object';
import { flatItemMapper } from '@ricoh/ria.client.core/utils/mapper/flat-item';

import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type {
  BatchOptions,
  DocumentOptions,
  ImportConditionRule,
  ImportSettingsOutlook,
  ProcessFlowStepTask,
  ProcessFlowStepTaskConfirmation,
  ProcessFlowStepTaskImport,
  ProcessFlowStepTaskNewBatch,
  ProcessFlowStepTaskReview,
  ProcessFlowStepTaskRoutingCloud,
  ProcessFlowStepTaskRoutingDocuware,
  ProcessFlowStepTaskValidation,
  RoutingDocuwareCabinetField,
  RoutingExportTemplateField,
  RoutingFile,
  RoutingSettingsDocuware,
  TaskConnector,
  TriggerType,
} from '~/common/model/api/batch-type/process-flow';
import type { DataValue, DataValueNull } from '~/common/model/api/common';
import type { IntegrationTypeAI } from '~/common/model/api/integration';
import type { TransformPattern } from '~/services/server/api/common/mapper';

import { BATCH_OPTIONS, DOCUMENT_OPTIONS, METADATA_CREATE_WITH_DATA_VALUE } from '~/common/constants/feature/settings/batch-type-library';
import { INTEGRATION_TYPES_AI } from '~/common/constants/feature/settings/integrations';
import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';
import processFlowLogic from '~/common/logic/feature/settings/batch-type-library/process-flow';
import { prepareGroups } from '~/services/server/admin/group/mapper';
import { prepareUsers } from '~/services/server/admin/user/mapper';
import { prepareConditionOperator, preparePattern } from '~/services/server/api/common/mapper';
import { prepareIntegrationProvider } from '~/services/server/api/integration/mapper';

import type {
  TransformImportCondition,
  TransformImportOutlookActions,
  TransformImportSettingsOneDrive,
  TransformImportSettingsOutlook,
  TransformImportSettingsSftp,
  TransformPathNamingRule,
  TransformProcessFlowStepTask,
  TransformProcessFlowStepTaskConfirmation,
  TransformProcessFlowStepTaskImport,
  TransformProcessFlowStepTaskNewBatch,
  TransformProcessFlowStepTaskReview,
  TransformProcessFlowStepTaskRoutingCloud,
  TransformProcessFlowStepTaskRoutingDocuware,
  TransformProcessFlowStepTaskValidation,
  TransformRoutingFiles,
  TransformRoutingTemplateDetailsType,
  TransformTaskConnector,
  TransformTemplateDetailsField,
} from '../../_process-flow.type';

const TASK_CONNECTOR_TYPE_MAPPING: Record<IntegrationTypeAI, TransformTaskConnector['kind'][]> = {
  natifAI: [`separation`, `classification`],
  azureAI: [`separation`, `classification`],
};

/**
 * Prepare Batch Options.
 *
 * @param {string} batchOptions Transform BatchOptions.
 *
 * @returns {BatchOptions[]} Batch options.
 */
export function prepareBatchOptions(batchOptions: string): BatchOptions[] {
  return flatItemMapper.prepare<BatchOptions>(batchOptions, {
    emptyItem: 'none',
    transform: (option) => {
      if (BATCH_OPTIONS.includes(option)) {
        return option;
      }

      throw new ClientError({
        module: 'service',
        message: 'Invalid document option',
        cause: { option },
      });
    },
  });
}

/**
 * Prepare Document Options.
 *
 * @param {string} documentOptions Transform DocumentOptions.
 *
 * @returns {DocumentOptions[]} Document Options.
 */
export function prepareDocumentOptions(documentOptions: string): DocumentOptions[] {
  return flatItemMapper.prepare<DocumentOptions>(documentOptions, {
    emptyItem: 'none',
    transform: (option) => {
      if (DOCUMENT_OPTIONS.includes(option)) {
        return option;
      }

      throw new ClientError({
        module: 'service',
        message: 'Invalid document option',
        cause: { option },
      });
    },
  });
}

/**
 * Prepare TaskConnector.
 *
 * @param {string} id TaskConnector Id.
 * @param {TransformTaskConnector} connector Transform TaskConnector.
 *
 * @returns {TaskConnector | undefined} TaskConnector.
 */
function prepareTaskConnector(id: string, connector: TransformTaskConnector): TaskConnector | undefined {
  // * Migrate old connector (use AI Workflow) to new connector (use AI model)
  if (!connector.aiModel) {
    return {
      id,
      $is: connector.kind,
      aiModel: {
        provider: 'natifAI',
      },
    } as TaskConnector;
  }

  const integrationType = prepareIntegrationProvider(connector.aiModel.provider);
  if (!INTEGRATION_TYPES_AI.includes(integrationType)) {
    return undefined;
  }

  if ((integrationType === 'natifAI' || integrationType === 'azureAI') && TASK_CONNECTOR_TYPE_MAPPING[integrationType].includes(connector.kind)) {
    return {
      id,
      $is: connector.kind,
      aiModel: {
        provider: integrationType,
        aiModelKey: connector.aiModel.aiModelKey,
      },
    };
  }

  return undefined;
}

/**
 * Prepare Path Naming Rule.
 *
 * @param {TransformPathNamingRule} pathNamingRule Transform Path Naming Rule.
 *
 * @returns {Pattern[]} Path Naming Rule.
 */
function preparePathNamingRule(pathNamingRule: TransformPathNamingRule): Pattern[] {
  return Object.values(pathNamingRule).reduce<Pattern[]>((prev, transformedPatterns, index) => {
    if (index > 0) {
      prev.push({
        $is: 'const',
        type: 'text',
        value: '/',
      });
    }

    prev.push(...transformedPatterns.map(preparePattern));
    return prev;
  }, []);
}

/**
 * Prepare Routing Template Details Field.
 *
 * @param {[id: string, field: TransformTemplateDetailsField]} fieldEntry Routing Template Details Field Entry.
 *
 * @returns {RoutingExportTemplateField} Routing Template Details Field.
 */
function prepareRoutingTemplateDetailsField([id, field]: [id: string, field: TransformTemplateDetailsField]): RoutingExportTemplateField {
  return {
    id,
    displayName: field.displayName,
    value: (() => {
      if (field.attributeType === 'pattern') {
        return [preparePattern(JSON.parse(field.attributeName))];
      }

      const item: PatternFormFieldItemTag<TransformRoutingTemplateDetailsType> = {
        $is: 'tag',
        tabKey: field.attributeType,
        value: field.attributeName,
      };
      const transformationText = false;

      const createPattern = {
        globalVariable: () => patternLogic.createPattern.fromGlobalVariable(item, transformationText),
        batchField: () => patternLogic.createPattern.fromBatchField(item, 'document', transformationText),
        batchVariable: () => patternLogic.createPattern.fromBatchVariable(item, 'document', transformationText),
        documentField: () => patternLogic.createPattern.fromDocumentField(item, transformationText),
        documentVariable: () => patternLogic.createPattern.fromDocumentVariable(item, transformationText),
      }[field.attributeType];

      return [createPattern()];
    })(),
  };
}

/**
 * Prepare Routing Files.
 *
 * @param {TransformRoutingFiles | undefined} files Transform Routing Files.
 *
 * @returns {RoutingFile[]} Routing Files.
 */
function prepareRoutingFiles(files: TransformRoutingFiles | undefined): RoutingFile[] {
  return files
    ? Object.entries(files).reduce<RoutingFile[]>((prev, [fileId, file]) => {
        const namingRule = file.namingRule.map(preparePattern);

        if (file.type === 'pdf') {
          prev.push({
            $is: 'pdf',
            id: fileId,
            namingRule,
          });
        } else if (file.type === 'tiff') {
          prev.push({
            $is: 'tiff',
            id: fileId,
            namingRule,
            isSeparatePages: !!file.isSeparatePages,
          });
        } else if (file.type === 'csv') {
          prev.push({
            $is: 'csv',
            id: fileId,
            namingRule,
            separator: file.separator ?? ',',
            isMergeDocuments: file.isMergeDocuments ?? false,
            template: {
              id: file.template?.id ?? newKey(),
              fields: file.template?.templateDetails ? Object.entries(file.template.templateDetails).map(prepareRoutingTemplateDetailsField) : [],
            },
          });
        } else if (file.type === 'json') {
          prev.push({
            $is: 'json',
            id: fileId,
            namingRule,
            template: {
              id: file.template?.id ?? newKey(),
              fields: file.template?.templateDetails ? Object.entries(file.template.templateDetails).map(prepareRoutingTemplateDetailsField) : [],
            },
          });
        } else if (file.type === 'xml') {
          prev.push({
            $is: 'xml',
            id: fileId,
            namingRule,
            template: {
              id: file.template?.id ?? newKey(),
              fields: file.template?.templateDetails ? Object.entries(file.template.templateDetails).map(prepareRoutingTemplateDetailsField) : [],
            },
          });
        } else if (file.type === 'txt') {
          prev.push({
            $is: 'txt',
            id: fileId,
            namingRule,
            includeExtension: file.includeExtension,
            template: {
              id: file.template?.id ?? newKey(),
              values: file.template?.templateDetails ? Object.entries(file.template.templateDetails).map(prepareRoutingTemplateDetailsField) : [],
            },
          });
        } else {
          // Do nothing
        }

        return prev;
      }, [])
    : [];
}

/**
 * Create Prepare flat item.
 *
 * @template TType Data type.
 * @param {TType[]} all All items.
 *
 * @returns {Func<[flatItem: string], TType[]>} Prepare flat item.
 */
function createPrepareFlatItem<TType extends string>(all: TType[]): Func<[flatItem: string], TType[]> {
  return (flatItem) => {
    const items = flatItemMapper.prepare(flatItem);
    if (items.includes('all')) {
      return all;
    }

    return Array.from(
      items.reduce<Set<TType>>((prev, item) => {
        if (all.includes(item)) {
          prev.add(item);
        }

        return prev;
      }, new Set<TType>()),
    );
  };
}

/**
 * Prepare Type Trigger.
 *
 * @param {string} flatItem Trigger flat item.
 *
 * @returns {TriggerType[]} TriggerType.
 */
export const prepareTriggerTypes = createPrepareFlatItem<TriggerType>(['fileCreated', 'fileModified']);

/**
 * Prepare Import OneDrive.
 *
 * @param {Except<ProcessFlowStepTaskImport, 'settings'>} base Except<ProcessFlowStepTaskImport, 'settings'>.
 * @param {TransformImportSettingsOneDrive} step TransformImportSettingsOneDrive.
 *
 * @returns {ProcessFlowStepTaskImport} ProcessFlowStepTaskImport .
 */
function prepareImportOneDrive(base: Except<ProcessFlowStepTaskImport, 'settings'>, step: TransformImportSettingsOneDrive): ProcessFlowStepTaskImport {
  return {
    ...base,
    settings: {
      $is: 'import',
      destination: {
        integration: {
          $is: 'microsoft',
          providerKey: step.destination.integration.providerKey,
        },
        folder: step.destination.folder,
        namingRule: preparePathNamingRule(step.destination.namingRule),
      },
      maxFiles: step.maxFilesPerBatch,
      interval: step.interval,
      fileMasks: step.fileMasks,
      triggerTypes: prepareTriggerTypes(step.triggerTypes),
      afterImport: {
        action: step.actionTypes,
        namingRule: step.afterImportFolderNamingRule ? preparePathNamingRule(step.afterImportFolderNamingRule) : undefined,
      },
    },
  };
}

/**
 * Transform After Import Actions.
 *
 * @param {TransformImportOutlookActions[]} afterImportActions After Import Actions.
 *
 * @returns {ImportSettingsOutlook[]} ImportSettingsOutlook.
 */
function prepareAfterImportActions(afterImportActions: TransformImportOutlookActions[] | undefined): ImportSettingsOutlook['afterImport'] {
  if (!afterImportActions) {
    return [];
  }

  return afterImportActions.flatMap((action) => {
    if (action.type === 'moveEmail') {
      return {
        $is: 'moveEmail',
        namingRule: action.namingRule ? preparePathNamingRule(action.namingRule) : [],
      };
    }

    if (action.type === 'assignCategory') {
      return {
        $is: 'assignCategory',
        namingRule: action.namingRule ? action.namingRule.map(preparePattern) : [],
      };
    }

    if (action.type === 'deleteEmail') {
      return {
        $is: 'deleteEmail',
      };
    }

    throw new ClientError({
      module: 'service',
      message: 'Invalid after import action type',
      cause: { action },
    });
  });
}

/**
 * Prepare Import Outlook.
 *
 * @param {Except<ProcessFlowStepTaskImport, 'settings'>} base Except<ProcessFlowStepTaskImport, 'settings'>.
 * @param {TransformImportSettingsOutlook} step TransformImportSettingsOutlook.
 *
 * @returns {ProcessFlowStepTaskImport} ProcessFlowStepTaskImport .
 */
function prepareImportOutlook(base: Except<ProcessFlowStepTaskImport, 'settings'>, step: TransformImportSettingsOutlook): ProcessFlowStepTaskImport {
  const buildConditionPattern = (item: TransformImportCondition): ImportConditionRule => {
    if ('kind' in item) {
      return {
        group: item.kind,
        rules: item.rules?.map(buildConditionPattern) ?? [],
      };
    }

    return {
      field: item.left.path ?? '',
      operator: prepareConditionOperator(item.operator) ?? 'equals',
      value: (() => {
        //* Prepare metadata value from data value
        if (METADATA_CREATE_WITH_DATA_VALUE.includes(item.left.path)) {
          return item.right as DataValue | DataValueNull;
        }

        //* Prepare pattern value metadata attachments_count or file_size from data value
        if (item.left.path === 'attachments_count' || item.left.path === 'file_size') {
          return (
            [
              {
                type: 'text',
                value: (item.right as DataValue).value,
              },
            ] as TransformPattern[]
          ).map(preparePattern);
        }

        //* Prepare other pattern from Transform Pattern
        return ([item.right] as TransformPattern[]).map(preparePattern);
      })(),
    };
  };

  return {
    ...base,
    settings: {
      $is: 'import',
      destination: {
        integration: {
          $is: 'outlook',
          providerKey: step.destination.integration.providerKey,
        },
        folder: step.destination.folder,
      },
      maxFiles: step.maxFilesPerBatch,
      fileMasks: step.fileMasks,
      includeImageOption: step.includeImageOption,
      condition: step.pattern
        ? buildConditionPattern(step.pattern)
        : {
            group: 'and',
            rules: [],
          },
      extraction: step.extraction
        ? step.extraction.map((extractionItem) => ({
            metadata: extractionItem.input.path,
            field: extractionItem.transformations.map((item) => preparePattern(item.target)),
          }))
        : [],
      afterImport: prepareAfterImportActions(step.afterImportActions),
    },
  };
}

/**
 * Prepare Import SFTP.
 *
 * @param {Except<ProcessFlowStepTaskImport, 'settings'>} base Except<ProcessFlowStepTaskImport, 'settings'>.
 * @param {TransformImportSettingsSftp} step TransformImportSettingsSftp.
 *
 * @returns {ProcessFlowStepTaskImport} ProcessFlowStepTaskImport .
 */
function prepareImportSftp(base: Except<ProcessFlowStepTaskImport, 'settings'>, step: TransformImportSettingsSftp): ProcessFlowStepTaskImport {
  return {
    ...base,
    settings: {
      $is: 'import',
      destination: {
        integration: {
          $is: 'sftp',
          providerKey: step.destination.integration.providerKey,
        },
        folder: step.destination.folder,
        namingRule: preparePathNamingRule(step.destination.namingRule),
      },
      includedSubFolders: step.includedSubFolders,
      maxFiles: step.maxFilesPerBatch,
      interval: step.interval,
      fileMasks: step.fileMasks,
      afterImport: {
        action: step.actionTypes,
        namingRule: step.afterImportFolderNamingRule ? preparePathNamingRule(step.afterImportFolderNamingRule) : undefined,
      },
    },
  };
}

/**
 * Prepare Routing Docuware Cabinet Fields.
 *
 * @param {TransformProcessFlowStepTaskRoutingDocuware} step Transform ProcessFlow Step TaskRouting Docuware.
 *
 * @returns {RoutingDocuwareCabinetField[]} RoutingDocuwareCabinetField array.
 */
function prepareCabinetFields(step: TransformProcessFlowStepTaskRoutingDocuware): RoutingDocuwareCabinetField[] {
  if (step.destination.location.type !== 'cabinet') {
    return [];
  }

  if (!('fields' in step) || !Array.isArray(step.fields)) {
    return [];
  }

  return step.fields.map<RoutingDocuwareCabinetField>((field) => ({
    $is: field.kind,
    name: field.name,
    value: field.value.map(preparePattern),
  }));
}

const prepare = {
  newBatch: (id: string, step: TransformProcessFlowStepTaskNewBatch): ProcessFlowStepTaskNewBatch => ({
    $is: 'task',
    id,
    name: step.name,
    status: step.status,
    settings: {
      $is: 'newBatch',
      batchOptions: prepareBatchOptions(step.batchOptions),
      documentOptions: prepareDocumentOptions(step.documentOptions),
      users: prepareUsers(step.users ?? {}),
      groups: prepareGroups(step.groups ?? {}),

      connectors: step.connectors
        ? Object.entries(step.connectors).reduce<TaskConnector[]>((prev, [connectorId, connector]) => {
            const taskConnector = prepareTaskConnector(connectorId, connector);
            if (taskConnector) {
              prev.push(taskConnector);
            }

            return prev;
          }, [])
        : [],
    },
    attributes: step.attributes,
  }),

  review: (id: string, step: TransformProcessFlowStepTaskReview): ProcessFlowStepTaskReview => ({
    $is: 'task',
    id,
    name: step.name,
    status: step.status,
    settings: {
      $is: 'review',
      documentOptions: prepareDocumentOptions(step.documentOptions),
      users: prepareUsers(step.users ?? {}),
      groups: prepareGroups(step.groups ?? {}),
    },
    attributes: step.attributes,
  }),

  validation: (id: string, step: TransformProcessFlowStepTaskValidation): ProcessFlowStepTaskValidation => ({
    $is: 'task',
    id,
    name: step.name,
    status: step.status,
    settings: {
      $is: 'validation',
      documentOptions: prepareDocumentOptions(step.documentOptions),
      users: prepareUsers(step.users ?? {}),
      groups: prepareGroups(step.groups ?? {}),
    },
    attributes: step.attributes,
  }),

  confirmation: (id: string, step: TransformProcessFlowStepTaskConfirmation): ProcessFlowStepTaskConfirmation => ({
    $is: 'task',
    id,
    name: step.name,
    status: step.status,
    settings: {
      $is: 'confirmation',
      users: prepareUsers(step.users ?? {}),
      groups: prepareGroups(step.groups ?? {}),
    },
    attributes: step.attributes,
  }),

  routingCloud: (id: string, step: TransformProcessFlowStepTaskRoutingCloud): ProcessFlowStepTaskRoutingCloud => ({
    $is: 'task',
    id,
    name: step.name,
    status: step.status,
    settings: {
      $is: 'routing',
      destination: {
        integration: {
          $is: prepareIntegrationProvider(step.destination.integration.provider),
          providerKey: step.destination.integration.providerKey,
        },
        folder: step.destination.folder,
        namingRule: preparePathNamingRule(step.destination.namingRule),
      },
      files: prepareRoutingFiles(step.files),
    },
    attributes: step.attributes,
  }),

  routingDocuware: (id: string, step: TransformProcessFlowStepTaskRoutingDocuware): ProcessFlowStepTaskRoutingDocuware =>
    ({
      $is: 'task',
      id,
      name: step.name,
      status: step.status,
      settings: {
        $is: 'routing.docuware',
        destination: {
          integration: {
            $is: prepareIntegrationProvider<'docuware'>(step.destination.integration.provider),
            providerKey: step.destination.integration.providerKey,
          },
          location: {
            $is: step.destination.location.type,
            ...omit(step.destination.location, 'type'),
          },
        } as RoutingSettingsDocuware['destination'],
        fields: prepareCabinetFields(step),
        files: prepareRoutingFiles(step.files),
      },
      attributes: step.attributes,
    }) as ProcessFlowStepTaskRoutingDocuware,

  import: (id: string, step: TransformProcessFlowStepTaskImport): ProcessFlowStepTaskImport | undefined => {
    const base: Except<ProcessFlowStepTaskImport, 'settings'> = {
      $is: 'task',
      id,
      name: step.name,
      attributes: step.attributes,
      status: step.status,
    };

    if (processFlowLogic.step.task.import.isTransformImportSettings<TransformImportSettingsOneDrive>(step, 'Microsoft')) {
      return prepareImportOneDrive(base, step);
    }

    if (processFlowLogic.step.task.import.isTransformImportSettings<TransformImportSettingsOutlook>(step, 'Outlook')) {
      return prepareImportOutlook(base, step);
    }

    if (processFlowLogic.step.task.import.isTransformImportSettings<TransformImportSettingsSftp>(step, 'Sftp')) {
      return prepareImportSftp(base, step);
    }

    return undefined;
  },
};

/**
 * Prepare Step Task.
 *
 * @param {string} id Step id.
 * @param {TransformProcessFlowStepTask} step Transform Step Task.
 *
 * @returns {ProcessFlowStepTask | undefined} Step Task.
 */
export function prepareStepTask(id: string, step: TransformProcessFlowStepTask): ProcessFlowStepTask | undefined {
  switch (step.kind) {
    case 'task.newBatch': {
      return prepare.newBatch(id, step);
    }

    case 'task.review': {
      return prepare.review(id, step);
    }

    case 'task.validation': {
      return prepare.validation(id, step);
    }

    case 'task.confirmation': {
      return prepare.confirmation(id, step);
    }

    case 'task.routing': {
      return prepare.routingCloud(id, step);
    }

    case 'task.routing.docuware': {
      return prepare.routingDocuware(id, step);
    }

    case 'task.import': {
      return prepare.import(id, step);
    }

    default: {
      return undefined;
    }
  }
}
